import {useCallback, useMemo, useState} from 'react';
import type {FocusEvent, KeyboardEvent, MouseEvent} from 'react';
import {useSelector} from 'react-redux';
import {ChevronDown, ChevronRight} from 'lucide-react';
import {ICON_SIZE, KEY} from 'components/common/constants';
import type {OrganisationChartNode} from 'types/modals';
import type {
    OrgChartNodeList,
    OrgChartTreeGroupProps,
    OrgChartTreeNodeProps,
    OrgChartTreeProps,
    OrgChartTreeState
} from 'types/organisationChart';
import * as reduxSelectors from '../store/application.reducers';

// every unit of the tree, whichever level it stands on - and only the units on show, since a
// closed unit does not draw the ones under it
const TREE_ITEM = '[role="treeitem"]';

// what a tree opens on, and goes back to once a new date brings a new one
const NONE_EXPANDED: ReadonlySet<string> = new Set();

const nodeKey = (node: OrganisationChartNode) => String(node.id);

/** The unit's name, which is what the unit is called - the units under it are no part of that. */
const labelId = (key: string) => `organisation-chart-node-${key}`;

/**
 * The units the endpoint handed along with a unit, as a list or as a level of its own. A unit that
 * comes with them never asks for them (see toggle), so they are drawn from here instead.
 */
const inlineChildren = (node: OrganisationChartNode): OrganisationChartNode[] | undefined => {
    const {children} = node as {children?: OrganisationChartNode[] | OrgChartNodeList};
    return Array.isArray(children) ? children : children?.rows;
};

/** The unit as the classic tree spelled it: its code, then its name. */
const nodeLabel = (node: OrganisationChartNode) => `${node.item.organizationUnitCode} - ${node.item.name}`;

/**
 * The units under one, once it is opened.
 *
 * Each level is read off the store by the unit it hangs under rather than handed down, so a level
 * landing redraws the unit it belongs to and leaves the rest of the tree as it is. The units the
 * endpoint handed along with the unit stand in for a level the store never loads.
 */
const OrgChartTreeGroup = ({nodeId, inlineRows, level, tree}: OrgChartTreeGroupProps) => {
    const loaded: OrgChartNodeList | undefined = useSelector(
        (store) => reduxSelectors.getOrgChartChildNodes(store)[nodeId]);
    const rows = loaded?.rows ?? inlineRows;

    // still on its way, or nothing under it after all
    if (!rows?.length) {
        return null;
    }

    return (
        <div role="group" className="new-organisation-chart__group">
            {rows.map(child =>
                <OrgChartTreeNode key={nodeKey(child)} node={child} level={level} tree={tree}/>)}
        </div>
    );
};

/**
 * One unit: a tree item the arrows walk to, which Enter, Space or a click selects. A unit with
 * units under it opens and closes with the chevron, or with the right and left arrows.
 */
export const OrgChartTreeNode = ({node, level, tree}: OrgChartTreeNodeProps) => {
    const key = nodeKey(node);
    const expandable = !node.leaf;
    const expanded = expandable && tree.expandedIds.has(key);
    const selected = tree.selectedId === key;

    const select = () => tree.select(node);

    // a click on a unit further down bubbles through this one, and is that unit's to answer
    const onClick = (event: MouseEvent<HTMLDivElement>) => {
        if ((event.target as HTMLElement).closest(TREE_ITEM) === event.currentTarget) {
            select();
        }
    };

    // the chevron opens the unit and leaves the selection where it was, as the classic tree had it
    const toggle = (event: MouseEvent<HTMLSpanElement>) => {
        event.stopPropagation();
        tree.toggle(node);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        // the key landed on a unit further down, which is that unit's to answer
        if (event.target !== event.currentTarget) {
            return;
        }

        switch (event.key) {
            case KEY.ENTER:
            case KEY.SPACE:
                select();
                break;
            case KEY.ARROW_RIGHT:
                if (!expandable) {
                    return;
                }
                if (expanded) {
                    // already open: on to the first unit under it, if it has arrived
                    event.currentTarget.querySelector<HTMLElement>(`:scope > [role="group"] > ${TREE_ITEM}`)?.focus();
                } else {
                    tree.toggle(node);
                }
                break;
            case KEY.ARROW_LEFT:
                if (expanded) {
                    tree.toggle(node);
                } else {
                    // closed, or nothing to close: back up to the unit this one hangs under
                    event.currentTarget.parentElement?.closest<HTMLElement>(TREE_ITEM)?.focus();
                }
                break;
            default:
                return;
        }
        event.preventDefault();
    };

    return (
        <div role="treeitem"
            className="new-organisation-chart__node"
            data-node-id={key}
            aria-level={level}
            aria-selected={selected}
            aria-expanded={expandable ? expanded : undefined}
            aria-labelledby={labelId(key)}
            tabIndex={tree.tabStopId === key ? 0 : -1}
            onClick={onClick}
            onKeyDown={onKeyDown}>
            <div className={`new-organisation-chart__node-row${selected ? ' new-organisation-chart__node-row--selected' : ''}`}>
                {expandable
                    // the arrows open and close the unit from the keyboard, so the chevron is
                    // the mouse's alone and no stop of its own
                    ? <span className="new-organisation-chart__toggle" aria-hidden="true" onClick={toggle}>
                        {expanded
                            ? <ChevronDown size={ICON_SIZE.MEDIUM}/>
                            : <ChevronRight size={ICON_SIZE.MEDIUM}/>}
                    </span>
                    // a leaf keeps the chevron's room, so its name lines up with its siblings'
                    : <span className="new-organisation-chart__toggle" aria-hidden="true"/>
                }
                <span id={labelId(key)} className="new-organisation-chart__node-label" title={nodeLabel(node)}>
                    {nodeLabel(node)}
                </span>
            </div>

            {expanded &&
                <OrgChartTreeGroup nodeId={key} inlineRows={inlineChildren(node)} level={level + 1} tree={tree}/>}
        </div>
    );
};

/**
 * The organisation chart as a tree (the WAI-ARIA tree view pattern): one tab stop, with the up
 * and down arrows walking the units on show, Home and End jumping to the first and the last, and
 * the right and left arrows opening and closing a unit (see OrgChartTreeNode). A unit's children
 * are only asked for once it is opened.
 */
export const OrgChartTree = ({rows, selectedNode, label, loadChildren, onSelect, className}: OrgChartTreeProps) => {

    // which units are open, and where focus last stood, belong to the tree they were picked in: a
    // new date brings a new tree, whose units start out closed again
    const [treeRows, setTreeRows] = useState(rows);
    const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(NONE_EXPANDED);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    if (treeRows !== rows) {
        setTreeRows(rows);
        setExpandedIds(NONE_EXPANDED);
        setFocusedId(null);
    }

    const toggle = useCallback((node: OrganisationChartNode) => {
        const key = nodeKey(node);
        // asked for on every opening unless the endpoint handed the children along, as the
        // classic tree did - which also reloads the roles the Role filter offers for that unit
        if (!expandedIds.has(key) && !node.children) {
            loadChildren(node.id);
        }
        setExpandedIds(previous => {
            const next = new Set(previous);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, [expandedIds, loadChildren]);

    // The unit focus stands on is the tree's tab stop. It is only ever one that was on show when
    // focus reached it - a click on a unit focuses it, and closing a unit is done from that unit -
    // so Tab always has somewhere to land. Until then, it is the first unit.
    const tabStopId = focusedId ?? (rows.length > 0 ? nodeKey(rows[0]) : null);
    const selectedId = selectedNode ? nodeKey(selectedNode) : null;

    const tree = useMemo<OrgChartTreeState>(() => ({
        expandedIds,
        tabStopId,
        selectedId,
        toggle,
        select: onSelect
    }), [expandedIds, tabStopId, selectedId, toggle, onSelect]);

    const onFocus = useCallback((event: FocusEvent<HTMLDivElement>) => {
        const nodeId = (event.target as HTMLElement).closest<HTMLElement>(TREE_ITEM)?.dataset.nodeId;
        if (nodeId) {
            setFocusedId(nodeId);
        }
    }, []);

    // the units on show, in the order they read - a closed unit draws nothing under it, so the
    // document order is the order the arrows walk
    const onKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
        const items = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(TREE_ITEM));
        const current = items.indexOf(event.target as HTMLElement);
        if (current === -1) {
            return;
        }

        let next: HTMLElement | undefined;
        switch (event.key) {
            case KEY.ARROW_DOWN:
                next = items[current + 1];
                break;
            case KEY.ARROW_UP:
                next = items[current - 1];
                break;
            case KEY.HOME:
                next = items[0];
                break;
            case KEY.END:
                next = items[items.length - 1];
                break;
            default:
                return;
        }
        // the panel scrolls, and the arrows must not scroll it from under the unit they move to
        event.preventDefault();
        next?.focus();
    }, []);

    return (
        // focusable, as a tree must be, but no tab stop of its own: Tab lands on the one unit that
        // is (see tabStopId), and a key that lands on the tree itself is let go (see onKeyDown)
        <div role="tree"
            tabIndex={-1}
            aria-label={label}
            className={className ? `new-organisation-chart__tree ${className}` : 'new-organisation-chart__tree'}
            data-cy="organisationChart.tree"
            onFocus={onFocus}
            onKeyDown={onKeyDown}>
            {rows.map(node => <OrgChartTreeNode key={nodeKey(node)} node={node} level={1} tree={tree}/>)}
        </div>
    );
};

export default OrgChartTree;
